import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { errorMessage, get, patch, put } from '../../api/client';
import type { HistoryItem, HistoryList } from '../../api/types';
import { IconAlert, IconExternal, IconImage, IconPlus } from '../../components/icons';
import { DragHandle, SortableList } from '../../components/sortable';
import { PageHead, Spinner, Switch, useFeedback } from '../../components/ui';
import { useT } from '../../i18n';
import { HISTORY_FIELDS, LangChips, objectPosition } from './shared';

const LIST_KEY = ['pm-history', 'list'] as const;

/** La historia de Phelon & Moore: los hitos de la línea de tiempo de la landing, en el orden en que se ven. */
export function PmHistory() {
  const { t, tn } = useT();
  const qc = useQueryClient();
  const { toast } = useFeedback();
  const { data, isPending, error } = useQuery({ queryKey: LIST_KEY, queryFn: () => get<HistoryList>('/admin/pm/history') });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: LIST_KEY });
    void qc.invalidateQueries({ queryKey: ['pm-publish'] });
    void qc.invalidateQueries({ queryKey: ['overview'] });
  };

  // Los reordenamientos van en fila y solo el último pinta la respuesta: arrastrar dos veces
  // seguido no hace saltar la lista a un orden intermedio.
  const reorder = useMutation({
    mutationKey: ['pm-history', 'order'],
    scope: { id: 'pm-history-order' },
    mutationFn: (ids: string[]) => put<{ items: HistoryItem[] }>('/admin/pm/history/order', { ids }),
    onSuccess: (res) => {
      if (qc.isMutating({ mutationKey: ['pm-history', 'order'] }) <= 1) qc.setQueryData<HistoryList>(LIST_KEY, (old) => old && { ...old, items: res.items });
      void qc.invalidateQueries({ queryKey: ['pm-publish'] });
    },
    onError: (e) => {
      toast(errorMessage(e), 'error');
      refresh();
    },
  });

  const setVisible = async (h: HistoryItem, visible: boolean) => {
    try {
      const { item } = await patch<{ item: HistoryItem }>(`/admin/pm/history/${h.id}`, { visible });
      qc.setQueryData(['pm-history', h.id], item);
      toast(visible ? t('Ahora es visible (se ve en el sitio al publicar)') : t('Quedó oculto (sale del sitio al publicar)'));
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
    refresh();
  };

  const items = data?.items ?? [];

  return (
    <>
      <PageHead
        kicker={t('Landing')}
        title={t('Historia')}
        description={t('Los hitos de la línea de tiempo «Our history», en el orden en que se ven al bajar por la sección. Arrastra para cambiar el orden. Los cambios se ven después de publicar.')}
      >
        {data?.siteUrl && (
          <a className="btn" href={`${data.siteUrl}/#history`} target="_blank" rel="noreferrer">
            <IconExternal /> {t('Ver en el sitio')}
          </a>
        )}
        <Link to="/historia/nuevo" className="btn btn-primary">
          <IconPlus /> {t('Nuevo hito')}
        </Link>
      </PageHead>

      {isPending ? (
        <Spinner />
      ) : error || !data ? (
        <div className="notice error">{errorMessage(error)}</div>
      ) : items.length === 0 ? (
        <div className="panel empty-state">
          <strong>{t('Todavía no hay hitos.')}</strong>
          <Link to="/historia/nuevo" className="btn btn-primary">
            <IconPlus /> {t('Nuevo hito')}
          </Link>
        </div>
      ) : (
        <div className="panel" style={{ overflow: 'hidden' }}>
          <SortableList
            items={items}
            getId={(h) => h.id}
            onReorder={(next) => {
              qc.setQueryData<HistoryList>(LIST_KEY, (old) => old && { ...old, items: next });
              reorder.mutate(next.map((h) => h.id));
            }}
          >
            {(h, i, handle) => {
              const errors = h.issues.filter((x) => x.level === 'error').length;
              const warnings = h.issues.length - errors;
              return (
                <div className={`list-item news-row history-row ${h.visible ? '' : 'is-hidden'}`}>
                  <DragHandle {...handle} />
                  <span className="history-pos" aria-label={t('Lugar {n}', { n: i + 1 })}>
                    {i + 1}
                  </span>
                  <Link to={`/historia/${h.id}`} className="thumb thumb-cover" aria-hidden="true" tabIndex={-1}>
                    {h.image ? <img src={h.image.url} alt="" loading="lazy" style={{ objectPosition: objectPosition(h.crop) }} /> : <IconImage />}
                  </Link>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="row row-wrap" style={{ gap: 8 }}>
                      <Link to={`/historia/${h.id}`} className="news-title">
                        <span className="history-year">{h.year}</span> {h.texts.en.title || t('Sin título')}
                      </Link>
                      {!h.visible && <span className="badge">{t('Oculto')}</span>}
                      {errors > 0 && (
                        <span className="badge red" title={h.issues.map((x) => x.message).join(' · ')}>
                          <IconAlert width={12} /> {errors === 1 ? t('Falta algo') : t('Faltan {n} cosas', { n: errors })}
                        </span>
                      )}
                    </div>
                    <div className="faint news-meta history-text">{h.texts.en.text}</div>
                    <div className="row row-wrap" style={{ gap: 8, marginTop: 6 }}>
                      <LangChips texts={h.texts} fields={HISTORY_FIELDS} />
                      {warnings > 0 && (
                        <span
                          className="faint"
                          style={{ fontSize: 12 }}
                          title={h.issues
                            .filter((x) => x.level === 'warning')
                            .map((x) => x.message)
                            .join(' · ')}
                        >
                          <IconAlert width={12} style={{ verticalAlign: '-2px', color: 'var(--warn)' }} /> {tn(warnings, '{n} aviso', '{n} avisos')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="actions">
                    <Switch checked={h.visible} onChange={(v) => void setVisible(h, v)} label={h.visible ? t('Visible') : t('Oculto')} />
                    <Link to={`/historia/${h.id}`} className="btn btn-sm">
                      {t('Editar')}
                    </Link>
                  </div>
                </div>
              );
            }}
          </SortableList>
        </div>
      )}
      {data && items.length > 0 && (
        <p className="faint" style={{ fontSize: 13, marginTop: 12 }}>
          {t('El primero es el que se ve al llegar a la sección; cada tramo de scroll pasa al siguiente. Los ocultos no salen en el sitio.')}
        </p>
      )}
    </>
  );
}
